import Seo from "../components/Seo";
import { Download, FileText } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { FadeIn } from "../components/FadeIn";
import PageHeader from "../components/PageHeader";

const policies = [
  {
    title: "Enrolment",
    body: "How families join the service, including waiting list priority, enrolment through our online system, orientation visits and the information we hold about each child to keep them safe and known.",
    files: [
      { href: "/policies/enrolment-policy-2026.pdf", label: "Enrolment Policy (2026)" },
    ],
  },
  {
    title: "Fees & Accounts",
    body: "How fees are set and invoiced, the annual membership and account keeping fee, permanent and casual bookings, late pick-up expectations, and support for families experiencing financial difficulty.",
    files: [
      { href: "/policies/fees-policy-aug-2026.pdf", label: "Fees Policy (August 2026)" },
    ],
  },
  {
    title: "Health, Hygiene & Medical Conditions",
    body: "Illness exclusion, medication administration, allergy and medical condition management plans, and hygiene practices that keep children well.",
    files: [
      { href: "/policies/diabetes-management-policy-2024.pdf", label: "Diabetes Management Policy (2024)" },
      { href: "/policies/epilepsy-management-policy-2024.pdf", label: "Epilepsy Management Policy (2024)" },
    ],
  },
  {
    title: "Sleep & Rest",
    body: "How we support children's individual rest, relaxation and sleep needs within a school-age care setting, including quiet spaces and supervision practices.",
    files: [
      { href: "/policies/sleep-and-rest-policy.pdf", label: "Sleep and Rest Policy" },
    ],
  },
  {
    title: "Safety & Supervision",
    body: "Arrival and departure procedures, authorised collection, excursion risk management, emergency and evacuation procedures, and child-safe practices across the service.",
  },
  {
    title: "Emergency & Evacuation",
    body: "How emergencies and evacuations are managed and practised, including lockdown procedures, emergency contact arrangements and regular drills with children.",
    files: [
      { href: "/policies/evacuation-policy-procedure-2025.pdf", label: "Evacuation Policy and Procedure (2025)" },
    ],
  },
  {
    title: "Water Safety",
    body: "How children's safety around water is managed during water-based activities and excursions, including risk assessments, supervision ratios and educator responsibilities.",
    files: [
      { href: "/policies/water-safety-policy-2024.pdf", label: "Water Safety Policy (2024)" },
    ],
  },
  {
    title: "Child Protection",
    body: "Our commitment to keeping every child safe: child-safe recruitment and practices, mandatory reporting obligations, and how we respond to any concern about a child's safety or wellbeing.",
    files: [
      { href: "/policies/child-protection-policy-2024.pdf", label: "Child Protection Policy (2024)" },
    ],
  },
  {
    title: "Acceptance & Refusal of Authorisations",
    body: "When the service can accept or must refuse an authorisation - such as collection arrangements, medication or excursions - and how children's safety guides every one of those decisions.",
    files: [
      { href: "/policies/acceptance-refusal-policy-2021.pdf", label: "Acceptance & Refusal of Authorisations Policy (2021)" },
    ],
  },
  {
    title: "External Provider Transfer & Collection",
    body: "How children are safely transferred to and collected from external providers during programs such as the Telopea Activities Program, including sign-in and sign-out procedures and supervision.",
    files: [
      { href: "/policies/external-provider-transfer-collection-policy.pdf", label: "External Provider Transfer & Collection Policy" },
    ],
  },
  {
    title: "Students & Volunteers",
    body: "How students on placement and volunteers are welcomed, supervised and supported, including screening, induction and their roles and responsibilities within the service.",
    files: [
      { href: "/policies/student-volunteer-policy-2024.pdf", label: "Student and Volunteer Policy (2024)" },
    ],
  },
  {
    title: "Sun Safety",
    body: "A SunSmart service: hats and sunscreen for outdoor play, shaded spaces, and sun-safe role-modelling by educators - in line with Cancer Council ACT guidance.",
    files: [
      { href: "/policies/sun-safety-policy-2025.pdf", label: "Sun Safety Policy (2025)" },
    ],
  },
  {
    title: "Behaviour Guidance",
    body: "Positive, respectful guidance that teaches rather than punishes - helping children build self-regulation, empathy and problem-solving skills in a safe environment.",
  },
  {
    title: "Nutrition & Food Handling",
    body: "Healthy breakfast and afternoon tea options, safe food handling practices, allergy-aware procedures, and food experiences that celebrate the diversity of our community.",
    files: [
      { href: "/policies/nutrition-food-handling-policy-2025.pdf", label: "Nutrition and Food Handling Policy (2025)" },
    ],
  },
  {
    title: "Anti-Bias & Inclusion",
    body: "How we build a service where every child and family belongs: inclusive practice, respect for diversity in all its forms, and equitable participation for children with additional needs.",
    files: [
      { href: "/policies/anti-bias-inclusion-policy-2024.pdf", label: "Anti-Bias & Inclusion Policy (2024)" },
    ],
  },
  {
    title: "EALD (English as an Additional Language or Dialect)",
    body: "How we support children and families for whom English is an additional language or dialect, ensuring equitable participation and clear communication.",
    files: [
      { href: "/policies/eald-policy-2024.pdf", label: "EALD Policy (2024)" },
    ],
  },
  {
    title: "Privacy & Confidentiality",
    body: "How we collect, store, use and protect personal information about children and families, in line with Australian privacy legislation.",
    files: [
      { href: "/policies/privacy-confidentiality-policy.pdf", label: "Privacy and Confidentiality Policy" },
    ],
  },
  {
    title: "Complaints & Feedback",
    body: "We welcome feedback. This policy sets out how to raise a concern, how it will be handled fairly and promptly, and how complaints inform continuous improvement.",
  },
];

const Policies = () => (
  <div data-testid="policies-page">
    <Seo
      title="Policies & Procedures | Telopea Park OSHC"
      description="Read the policies and procedures that keep children safe and supported at Telopea Park OSHC, Barton, Canberra."
      path="/policies"
    />
    <PageHeader
      testId="policies-header"
      eyebrow="Policies"
      title="The standards behind the warmth"
      description="Our policies translate the National Law, National Regulations and National Quality Standard into everyday practice. Summaries are below - full policy documents are available to families on request."
    />

    <section className="mx-auto max-w-4xl px-5 sm:px-8 py-16 sm:py-20" data-testid="policies-list">
      <FadeIn>
        <Accordion type="single" collapsible className="space-y-4">
          {policies.map((p, i) => (
            <AccordionItem
              key={p.title}
              value={`policy-${i}`}
              className="rounded-2xl border border-slate-200 bg-white px-6 shadow-sm"
              data-testid={`policy-item-${i + 1}`}
            >
              <AccordionTrigger
                className="py-6 text-left font-display text-xl font-semibold text-navy hover:no-underline [&>svg]:text-waratah"
                data-testid={`policy-trigger-${i + 1}`}
              >
                <span className="flex items-center gap-3">
                  <FileText className="h-5 w-5 shrink-0 text-waratah" />
                  {p.title}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base sm:text-lg text-slate-600 leading-relaxed">
                <p>{p.body}</p>
                {p.files && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {p.files.map((f) => (
                      <a
                        key={f.href}
                        href={f.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`policy-download-${i + 1}-${f.label.split(" ")[0].toLowerCase()}`}
                        className="inline-flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
                      >
                        <Download className="h-4 w-4" /> {f.label} (PDF)
                      </a>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p className="mt-8 text-base text-slate-500" data-testid="policies-note">
          Full policy documents are available at the service and can be emailed to families on request. Policies are
          reviewed regularly with input from families, educators and children.
        </p>
      </FadeIn>
    </section>
  </div>
);

export default Policies;
